import { useState } from 'react';
import { AlertCircle, Mail, Trash2, Send, X, Save } from 'lucide-react';
import { UnifiedEmailRecord } from '@/types/emails';
import { formatEmailDate, formatPostmarkError } from './utils';
import { Button } from '@/components/ui';
import { useUpdateEmailAddress, useResendEmail, useDeleteEmailJob } from '@/hooks/useEmailQueries';

export const DetailsDialog = ({ jobData, isModalOpen, setIsModalOpen }: { jobData: UnifiedEmailRecord, isModalOpen: boolean, setIsModalOpen: (isModalOpen: boolean) => void  }) => {
  const [email, setEmail] = useState<string>(jobData.recipient_email);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  
  const { mutate: updateEmailAddress, isPending: updating } = useUpdateEmailAddress();
  const { mutate: resendEmail, isPending: resending } = useResendEmail();
  const { mutate: deleteEmailJob, isPending: deleting } = useDeleteEmailJob();


  const handleCloseModal = () => {
    setIsModalOpen(false);
    setShowDeleteConfirm(false);
    setEmail(jobData.recipient_email); // Reset email to original
  };


  const handleDelete = () => {
    if (showDeleteConfirm) {
      deleteEmailJob(jobData.id, {
        onSuccess: () => {
          setShowDeleteConfirm(false);
          setIsModalOpen(false);
        }
      });
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const handleResend = () => {
    if (email && email !== jobData.recipient_email) {
      updateEmailAddress(
        { jobId: jobData.id, newEmail: email },
        {
          onSuccess: () => {
            resendEmail(
              { jobId: jobData.id },
              { onSuccess: () => { setIsModalOpen(false) } }
            );
          },
          onError: () => { setEmail(jobData.recipient_email) }
        }
      );
    } else {
      resendEmail(
        { jobId: jobData.id },
        { onSuccess: () => { setIsModalOpen(false) } }
      );
    }
  };

  const handleSave = () => {
    if (email && email !== jobData.recipient_email) {
      updateEmailAddress(
        { jobId: jobData.id, newEmail: email },
        {
          onSuccess: () => {
            setEmail(email);
            setIsModalOpen(false);
          },
          onError: () => { setEmail(jobData.recipient_email) }
        }
      );
    }
  };

  return (
    <>
      {isModalOpen && (
        <div className=" fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                Job Details - Failed
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Failure Reason Section */}
              <div className="space-y-3 ">
                <h3 className="text-sm font-medium text-gray-900 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2 text-red-500" />
                  Failure Reason
                </h3>
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-sm text-red-700">
                    {formatPostmarkError(jobData.error_message || 'No failure reason provided')}
                  </p>
                </div>
                {jobData.generated_at && (
                  <div className="text-xs text-gray-500">
                    <p>Failed at: {formatEmailDate(jobData.generated_at)}</p>
                  </div>
                )}
              </div>
              {/* Email Change Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-blue-500" />
                  Change Email
                </h3>
                <div className="space-y-2">
                  <div className="block text-xs text-gray-600">
                    You can edit the email address and try resending it.
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e)=>setEmail(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-0 text-sm ${
                      email === jobData.recipient_email
                        ? 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                        : email === ''
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-green-300 focus:ring-green-500 focus:border-green-500'
                    }`}
                    placeholder="Enter new email address"
                  />
                </div>
              </div>

              {/* Delete Record Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900 flex items-center text-red-500">
                  {/* <Trash2 className="w-4 h-4 mr-2 text-red-500" /> */}
                  Delete Record
                </h3>
                  
                  <div className="flex items-center gap-2"> 
                    <Button
                      size="sm"
                      onClick={handleDelete}
                      variant={'outline'}
                      disabled={deleting}
                      className="text-red-500 border-red-500 hover:text-white hover:bg-red-500"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      {showDeleteConfirm ? 'Confirm Delete' : 'Delete Record'}
                    </Button>
                    {showDeleteConfirm && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={deleting}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={handleCloseModal}
              >
                Close
              </Button>
              {email !== jobData.recipient_email && email !== '' && (
                <Button
                  variant="default"
                  onClick={handleSave}
                  disabled={updating}
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  {updating ? 'Saving...' : 'Save'}
                </Button>
              )}
              <Button
                variant="default"
                onClick={handleResend}
                disabled={resending || updating}
              >
                <Send className="w-4 h-4 mr-1.5" />
                {resending || updating ? 'Processing...' : 'Resend'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};